import { HttpParams } from '@angular/common/http';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { Location } from 'src/app/models/configurators-2d/location';
import { Picture } from 'src/app/models/configurators-2d/picture';
import { Project } from 'src/app/models/project';
import { IAdministrationService } from './administrations/i-administration-service';
import { BaseApiService } from './base-api-service';
import { MODAL } from '../modal.service';
import { Discount } from 'src/app/models/configurators-2d/administrations/discounts/discount';
import { Ean } from 'src/app/models/configurators-2d/administrations/discounts/ean';
import { Parameters } from 'src/app/models/configurators-2d/applications/carpentry/Elcia/parameters';
import { Cdm } from 'src/app/models/configurators-2d/applications/carpentry/cdm';
import { Order } from 'src/app/models/configurators-2d/order';
import { ShoppingCartProductList } from 'src/app/models/shopping-cart/shopping-cart-product-list';
import { ShoppingCartProduct } from 'src/app/models/shopping-cart/shopping-cart-product';
import { logError } from '../helpers/sentry.helper';
import { parseUtcDateOrNull } from '../helpers/utc-date.helper';

export abstract class CommonApp2dService extends BaseApiService implements IAdministrationService {
  constructor(protected applicationName: CONFIG_CONTEXT) {
    super(applicationName);
  }

  /**
   * Stock l'information qu'une nouvelle configuration a été créée
   * @returns void
   */
  public newProjectOpen(): Promise<void> {
    const params = new HttpParams().set('distribId', this.distribId);
    return this.post(`${this.baseApiUrl}Creation`, null, params);
  }

  public saveProject(configuration: any): Promise<boolean> {
    if (configuration.id) {
      return this.put<boolean>(`${this.apiUrl}Configuration`, configuration);
    } else {
      return this.post<boolean>(`${this.apiUrl}Configuration`, configuration);
    }
  }

  /**
   * Fonction de récupération des projets client
   * @param id id du client
   * @returns liste des projets client
   */
  async getProjectsByUser(id: string): Promise<Project[]> {
    const params = new HttpParams().set('id', id);
    try {
      const res = await this.get<Project[]>(`${this.apiUrl}Configuration/Projects`, params);
      return res.map((project: any) => ({
        projectUUID: this.distribId + '_' + project.id,
        projectID: project.id,
        projectName: project.name,
        dateUpdate: parseUtcDateOrNull(project.lastModificationTime),
        search: project.name.toLowerCase(),
        thumb: project.hasThumbnail ? project.picture : this.apiUrl + project.picture,
        distributionID: this.distribId,
        description: '',
        extraDatas: project.extraDatas,
        hasDiscount: project.hasDiscount,
        hasQuotation: project.hasQuotation,
        quotationExpirationDate: project.quotationExpirationDate,
        inTunnel: project.inTunnel,
        tunnelId: project.tunnelId,
        tunnelContext: project.tunnelContext,
        is_locked: project.is_locked,
      }));
    } catch (error) {
      logError(error);
      return [];
    }
  }

  /**
   * Fonction de duplication du projet
   * @param id id du projet
   * @param title titre du nouveau projet
   * @returns configuration (typé en any pour l'utiliser aussi sur ClosetDoorsStandard)
   */
  duplicateProjectById(id: string, title: string): Promise<any> {
    const params = new HttpParams().set('id', id).set('title', title);
    return this.post(`${this.apiUrl}Configuration/Duplicate`, {}, params);
  }

  /**
   * Récupération du fichier XML
   * @param id id du projet
   * @returns fichier XML
   */
  async generateXML(configId: string): Promise<{ orderId: string, xml: string }> {
    const params = new HttpParams().set('id', configId);
    const res = await this.post<{ id: string, xmlBase64: string }>(`${this.apiUrl}Order`, {}, params);
    const bytes = Uint8Array.from(atob(res.xmlBase64), c => c.codePointAt(0));
    return { orderId: res.id, xml: new TextDecoder('utf-8').decode(bytes) };
  }

  async getPrevisit(configId: string): Promise<string> {
    const params = new HttpParams().set('id', configId);
    const res = await this.post(`${this.apiUrl}Order/Previsite`, {}, params);
    const bytes = Uint8Array.from(atob(res.xmlBase64), c => c.codePointAt(0));
    return new TextDecoder('utf-8').decode(bytes);
  }

  /**
   * Fonction de récupération des orders
   * @returns Promise
   */
  getOrders(id: string): Promise<Order[]> {
    const params = new HttpParams().set('id', id);
    return this.get<Order[]>(`${this.apiUrl}Order`, params);
  }

  /**
   * Fonction de mise à jour des orders
   * @returns Promise
   */
  updateOrder(order: Order): Promise<void> {
    return this.put(`${this.apiUrl}Order`, order);
  }

  /**
   * Fonction d'envoi de la commande fournisseurs via le backend
   * @param orderId identifiant de la commande
   */
  sendSupplier(orderId: string): Promise<void> {
    const params = new HttpParams().set('orderId', orderId);
    return this.put(`${this.apiUrl}Order/SendSupplier`, null, params);
  }

  /**
   * Fonction d'envoi forcé de la commande fournisseurs via le backend.
   * Réservée aux administrateurs côté API.
   */
  forceSendSupplier(orderId: string): Promise<void> {
    const params = new HttpParams().set('orderId', orderId);
    return this.put(`${this.apiUrl}Order/ForceSendSupplier`, null, params);
  }

  saveOpportunityNumber(orderId: string, opportunityNumber: number): Promise<void> {
    return this.post(`${this.apiUrl}Order/AssignOpportunityNumber`, {
      orderId,
      opportunityNumber
    });
  }

  /**
   * Fonction de récupération des environnements
   * @returns Promise
   */
  getLocations<L extends Location>(): Promise<L[]> {
    return this.get<L[]>(`${this.apiUrl}Configuration/Locations`);
  }

  /**
   * Fonction de suppression d'un projet
   * @param id id de la configuration
   * @returns promise
   */
  deleteProjectById(id: string): Promise<any> {
    const params = new HttpParams().set('configurationId', id);
    return this.delete(`${this.apiUrl}Configuration`, undefined, params);
  }

  /**
   * Récupération des images du configurateur pour les remplacées
   * @returns Liste des images utilisées sur le projet
   */
  getAdminPictures<P extends Picture>(): Promise<P[]> {
    return this.get<P[]>(`${this.apiUrl}Data/Pictures`);
  }

  /**
   * Changement d'une image
   * @param id Identifiant unique d'une image
   * @param file Fichier de remplacement pour l'image
   * @returns
   */
  setAdminPicture(id: number, file: File): Promise<any> {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);
    formData.append('id', id.toString());
    return this.post(`${this.apiUrl}Data/Pictures`, formData);
  }

  /**
   * Changement des prix
   * @param file Fichier de prix
   * @returns Résultat de l'import (ok ou pas)
   */
  setAdminPrices(file: File): Promise<any> {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);
    return this.post(`${this.apiUrl}Data`, formData);
  }

  getStatistics(start: Date, end: Date): Promise<any> {
    const startValue = start == null || start == undefined ? 'undefined' : start.toISOString();
    const endValue = end == null || end == undefined ? 'undefined' : end.toISOString();
    const params = new HttpParams().set('start', startValue).set('end', endValue);
    return this.get(`${this.apiUrl}Data/Statistics`, params, 'blob');
  }

  /**
   * Permet de récupérer les EANs de promotions
   */
  getEANs(): Promise<Ean[]> {
    return this.get<Ean[]>(this.apiUrl + 'Data/EAN');
  }

  /**
   * Permet de récupérer les EANs de promotions
   */
  getDiscounts(): Promise<Discount[]> {
    return this.get<Discount[]>(this.apiUrl + 'Data/Discounts');
  }

  addDiscount(startDate: string, endDate: string, eanId: string): Promise<Discount[]> {
    const body = [
      {
        startDate: startDate,
        endDate: endDate,
        eanId: eanId,
      },
    ];

    return this.post<Discount[]>(this.apiUrl + 'Data/Discounts', body);
  }

  removeDiscount(discountId: string): Promise<Discount[]> {
    const params = new HttpParams().set('discountId', discountId);
    return this.delete<Discount[]>(this.apiUrl + 'Data/Discounts', params);
  }

  getCdmUrl(): Promise<Cdm> {
    const body: Parameters = {
      organisation: 'RACINE;ENS1;AAA;A1',
      userName: 'VEND-DOE',
    };

    return this.post<Cdm>(this.apiUrl + 'Administration/Cdm', body);
  }

  /**
   * Fonction d'ouverture de la modal d'alerte en cas d'erreur
   * @param message message de l'erreur
   */
  openErrorModal(message: string) {
    this.modalService.display(MODAL.GENERIC_ERROR, { errorMessage: message });
  }

  /**
   * Fonction d'ajout d'une liste de produits dans la configuration
   * @param productList
   * @returns
   */
  addProductListToConfiguration(productList: ShoppingCartProductList): void {
    return;
  }

  /**
   * Fonction de mise à jour du orderNumber et du supplierSent
   * @param product
   * @returns
   */
  updateProductOrder(product: ShoppingCartProduct): Promise<void> {
    return Promise.resolve();
  }
}
